import { motion } from "framer-motion";
import { BadgeCheck, MapPin, Building2, X, ArrowDown } from "lucide-react";
import { PARTNERS } from "../lib/network";
import { projectsByState } from "../lib/site";

const CAT_COLORS = {
  "Elite Super Stockist": "#E8C15A",
  "Super Stockist": "#B87333",
  "Wholesaler": "#2FB999",
  "Dealer": "#5B8DEF",
  "Booking Agent": "#D8B23E",
  "Civil Contractor": "#C0654E",
  "Supplier": "#9B7BD8",
};

export const StateDeepDive = ({ state, onViewAll, onClose }) => {
  const partners = PARTNERS.filter((p) => p.state === state);
  const topPartners = [...partners]
    .sort((a, b) => (Number(b.featured || 0) - Number(a.featured || 0)) || (b.verified - a.verified) || (a.since - b.since))
    .slice(0, 6);
  const projects = projectsByState(state);

  return (
    <motion.div
      key={state}
      data-testid="state-deep-dive"
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      className="mt-12 border border-[#B87333]/25 rounded-sm bg-white/[0.02] overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4 px-6 md:px-8 py-6 border-b border-white/10">
        <div>
          <span className="overline text-[#B87333]">/ State deep-dive</span>
          <h3 className="font-serif text-3xl md:text-4xl mt-3">Inside {state}</h3>
          <p className="font-body text-sm text-[#8A8A82] mt-2">
            {partners.length} partner{partners.length === 1 ? "" : "s"} · {projects.length} headline programme{projects.length === 1 ? "" : "s"}
          </p>
        </div>
        <button
          data-testid="deep-dive-close"
          onClick={onClose}
          className="shrink-0 grid place-items-center w-9 h-9 rounded-full border border-white/15 text-[#8A8A82] hover:text-[#F5F5F0] hover:border-[#B87333] transition-colors"
          aria-label="Close state deep-dive"
        >
          <X size={16} />
        </button>
      </div>

      <div className="grid lg:grid-cols-2 gap-px bg-white/[0.06]">
        {/* Headline projects */}
        <div className="bg-[#070707] p-6 md:p-8" data-testid="deep-dive-projects">
          <div className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] mb-5">Headline projects</div>
          {projects.length === 0 ? (
            <p className="font-body text-sm text-[#8A8A82]" data-testid="deep-dive-projects-empty">
              Programme references for {state} are available to verified partners on request.
            </p>
          ) : (
            <div className="space-y-4">
              {projects.map((p) => (
                <div key={p.project} className="border-b border-white/[0.06] pb-4 last:border-0 last:pb-0">
                  <div className="flex items-start justify-between gap-3">
                    <span className="font-serif text-lg text-[#F5F5F0] leading-snug">{p.project}</span>
                    <span className={`font-grotesk text-[9px] uppercase tracking-[0.16em] shrink-0 mt-1 ${p.status === "Delivered" ? "text-[#2FB999]" : "text-[#D8B23E]"}`}>{p.status}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-body text-xs text-[#8A8A82] mt-2">
                    <span className="inline-flex items-center gap-1"><MapPin size={11} /> {p.location}</span>
                    <span className="text-[#6f6f68]">·</span>
                    <span>{p.material}</span>
                    <span className="text-[#6f6f68]">·</span>
                    <span className="text-[#C9C9C2]">{p.tonnage}</span>
                    <span className="text-[#6f6f68]">·</span>
                    <span>{p.year}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top partners */}
        <div className="bg-[#070707] p-6 md:p-8" data-testid="deep-dive-partners">
          <div className="flex items-center justify-between mb-5">
            <span className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82]">Top partners</span>
            {partners.length > topPartners.length && (
              <button
                data-testid="deep-dive-view-all"
                onClick={onViewAll}
                className="inline-flex items-center gap-1.5 font-grotesk text-[10px] uppercase tracking-[0.16em] text-[#B87333] hover:text-[#F5F5F0] transition-colors"
              >
                View all {partners.length} <ArrowDown size={12} />
              </button>
            )}
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {topPartners.map((p) => (
              <div key={p.id} className="border border-white/10 rounded-sm p-4 hover:border-[#B87333]/50 transition-colors">
                <div className="flex items-start justify-between gap-2">
                  <span className="grid place-items-center w-8 h-8 rounded-full bg-[#141414] border border-white/10 text-[#B87333] shrink-0"><Building2 size={14} /></span>
                  {p.verified && <BadgeCheck size={15} className="text-[#2FB999] mt-1" />}
                </div>
                <div className="font-serif text-base mt-3 leading-snug">{p.name}</div>
                {p.trade && <div className="font-body text-[11px] text-[#B87333] mt-1">{p.trade}</div>}
                <div className="flex items-center gap-1.5 font-body text-[11px] text-[#8A8A82] mt-1.5"><MapPin size={11} /> {p.city}</div>
                <div className="mt-3">
                  <span className="font-grotesk text-[9px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full" style={{ color: CAT_COLORS[p.category], border: `1px solid ${CAT_COLORS[p.category]}44` }}>
                    {p.category}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};
